import React from 'react';

export const ComprehensiveDisclaimer: React.FC = () => {
    return (
        <div className="max-w-4xl mx-auto text-gray-300">
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">Legal Disclaimer</h1>
                <p className="text-yellow-400 font-semibold">Aureus Alliance Holdings (Pty) Ltd</p>
                <div className="text-sm text-white mt-2">
                    Last updated: December 2024 | Version 2.0
                </div>
            </div>
            
            <div className="space-y-6 leading-relaxed">
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
                    <h3 className="text-red-400 font-bold mb-2">⚠️ IMPORTANT LEGAL NOTICE</h3>
                    <p className="text-sm">
                        <strong>READ THIS DISCLAIMER CAREFULLY BEFORE USING OUR PLATFORM OR PURCHASING SHARES.</strong> 
                        By accessing our website or purchasing shares, you acknowledge that you have read, understood, 
                        and agree to be bound by this disclaimer.
                    </p>
                </div>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">1. NOT FINANCIAL ADVICE</h2>
                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 space-y-3">
                        <h3 className="text-yellow-400 font-bold">🚫 NO FINANCIAL ADVICE PROVIDED</h3>
                        <div className="space-y-2 text-sm">
                            <p>
                                <strong>Aureus Alliance Holdings (Pty) Ltd does not provide financial advice, investment advice, 
                                or recommendations.</strong> All information on our platform is for informational and educational purposes only.
                            </p>
                            <p>
                                <strong>One share will not make you rich.</strong> Share purchases should be considered as part of a 
                                diversified shareholding. Past performance does not guarantee future results.
                            </p>
                            <p>
                                <strong>Company shares are NOT a replacement for traditional financial products including:</strong>
                            </p>
                            <ul className="list-disc list-inside ml-4 space-y-1">
                                <li>Life insurance policies or life cover</li>
                                <li>Funeral insurance or burial schemes</li>
                                <li>Pension funds or retirement annuities</li>
                                <li>Medical aid or health insurance</li>
                                <li>Government grants or social benefits</li>
                                <li>Emergency savings or liquid investments</li>
                                <li>Short-term insurance (car, home, etc.)</li>
                                <li>Disability or income protection insurance</li>
                            </ul>
                            <p>
                                <strong>Always consult qualified professionals:</strong> Seek advice from licensed financial advisors, 
                                tax professionals, and legal counsel before buying shares.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">2. SHARE CERTIFICATE PROCESSING</h2>
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                        <h3 className="text-blue-400 font-bold mb-2">📋 CIPC Certificate Timeline</h3>
                        <div className="space-y-2 text-sm">
                            <p>
                                <strong>Share certificates will be available 10-14 business days after purchase completion,</strong> 
                                provided that KYC (Know Your Customer) verification has been completed and approved.
                            </p>
                            <ul className="list-disc list-inside ml-4 space-y-1">
                                <li>Certificates are issued by CIPC (Companies and Intellectual Property Commission)</li>
                                <li>KYC approval is mandatory before certificate processing</li>
                                <li>Processing times may vary due to CIPC workload or public holidays</li>
                                <li>Digital certificates will be available through your dashboard</li>
                                <li>Physical certificates available upon request for additional fee</li>
                            </ul>
                            <p>
                                <strong>The Company is not responsible for delays in CIPC processing beyond our control.</strong>
                            </p>
                        </div>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">3. MINING OPERATION RISKS</h2>
                    <div className="space-y-4">
                        <p><strong>Gold mining operations involve numerous significant risks that can affect profitability and share value:</strong></p>

                        <h3 className="text-lg font-semibold text-white">Operational Mining Risks</h3>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li><strong>Equipment failure:</strong> Heavy machinery breakdowns, maintenance costs, replacement expenses</li>
                            <li><strong>Production delays:</strong> Weather conditions, technical problems, supply chain disruptions</li>
                            <li><strong>Safety incidents:</strong> Mining accidents, worker injuries, operational shutdowns</li>
                            <li><strong>Labor disputes:</strong> Strikes, wage negotiations, skills shortages</li>
                            <li><strong>Power supply issues:</strong> Electricity outages, load shedding, energy cost increases</li>
                            <li><strong>Water availability:</strong> Drought conditions, water rights, processing limitations</li>
                            <li><strong>Transportation challenges:</strong> Road access, logistics costs, infrastructure damage</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-white">Environmental and Geological Risks</h3>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li><strong>Ore grade variations:</strong> Lower than expected gold content, resource depletion</li>
                            <li><strong>Geological surprises:</strong> Unexpected rock formations, water ingress, ground instability</li>
                            <li><strong>Environmental compliance:</strong> Pollution control costs, rehabilitation requirements</li>
                            <li><strong>Climate change impacts:</strong> Extreme weather, flooding, drought conditions</li>
                            <li><strong>Biodiversity protection:</strong> Protected species, habitat preservation requirements</li>
                            <li><strong>Waste management:</strong> Tailings storage, toxic material handling, long-term liability</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-white">Regulatory and Legal Risks</h3>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li><strong>Mining license changes:</strong> Permit renewals, license conditions, regulatory compliance</li>
                            <li><strong>Environmental regulations:</strong> New environmental laws, stricter compliance requirements</li>
                            <li><strong>Taxation changes:</strong> Mining royalties, corporate tax rates, export duties</li>
                            <li><strong>Land rights disputes:</strong> Community claims, traditional land rights, expropriation</li>
                            <li><strong>Government policy:</strong> Nationalization risks, foreign ownership restrictions</li>
                            <li><strong>Legal challenges:</strong> Environmental lawsuits, community disputes, regulatory penalties</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-white">Market and Economic Risks</h3>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li><strong>Gold price volatility:</strong> International gold prices can fluctuate dramatically</li>
                            <li><strong>Currency fluctuations:</strong> USD/ZAR exchange rate impacts on gold sales revenue</li>
                            <li><strong>Inflation impacts:</strong> Rising costs of fuel, electricity, labor, and materials</li>
                            <li><strong>Interest rate changes:</strong> Financing costs, capital investment impacts</li>
                            <li><strong>Economic downturns:</strong> Reduced demand, financing difficulties, market instability</li>
                            <li><strong>Commodity market cycles:</strong> Boom and bust cycles affecting mining profitability</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-white">Company-Specific Risks</h3>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li><strong>Early-stage operations:</strong> Limited operating history, unproven reserves</li>
                            <li><strong>Management dependency:</strong> Reliance on key personnel, succession planning</li>
                            <li><strong>Capital requirements:</strong> Need for ongoing share capital, financing challenges</li>
                            <li><strong>Technology risks:</strong> Equipment obsolescence, technological changes</li>
                            <li><strong>Insurance limitations:</strong> Uninsurable risks, coverage gaps, premium increases</li>
                            <li><strong>Reputation risks:</strong> Environmental incidents, community relations, media coverage</li>
                        </ul>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">4. DIVIDEND & PROFIT DISTRIBUTION</h2>
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-white">Profit Sharing Guarantee</h3>
                        <p className="text-sm">
                            All shareholders are guaranteed to receive their proportional share of company profits generated from mining operations and other business ventures. However, the amount of dividends will vary based on actual company performance and profitability.
                        </p>

                        <h3 className="text-lg font-semibold text-white">Dividend Calculation Basis</h3>
                        <p className="text-sm">Dividend payments are calculated and distributed as follows:</p>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li>Dividends are calculated according to actual company profit after all operational expenses</li>
                            <li>Profits are generated from resources actually mined and sold, plus other business ventures</li>
                            <li>Each shareholder receives dividends proportional to their shareholding percentage</li>
                            <li>Dividend amounts will vary based on company performance - there are no guaranteed percentage-based returns</li>
                            <li>Dividend declarations and timing are at the discretion of the Board of Directors</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-white">Factors Affecting Dividend Amounts</h3>
                        <p className="text-sm">The amount of dividends distributed will depend on various factors including:</p>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li>Mining production volumes and actual gold extracted</li>
                            <li>Gold market prices at the time of sale</li>
                            <li>Operational costs including mining, processing, labor, and overhead expenses</li>
                            <li>Capital expenditures for equipment, facilities, and expansion projects</li>
                            <li>Regulatory compliance costs for environmental, safety, and legal requirements</li>
                            <li>Debt servicing including loan repayments and interest expenses</li>
                            <li>Tax obligations and other statutory requirements</li>
                            <li>Reserves maintained for future operations and business development</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-white">Important Disclaimers</h3>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li><strong>No guaranteed percentage returns:</strong> While shareholders are guaranteed to receive profits, there are no guaranteed percentage-based returns on share ownership</li>
                            <li><strong>Variable dividend amounts:</strong> Dividend payments will vary based on actual business performance and may be small during challenging periods</li>
                            <li><strong>Projection estimates:</strong> Any dividend projections or estimates provided are for illustrative purposes only and actual results may differ significantly</li>
                            <li><strong>Business performance dependency:</strong> All dividend payments are dependent on the Company generating positive cash flows and profits</li>
                        </ul>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">5. LIMITATION OF LIABILITY</h2>
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-white">Maximum Liability Limitation</h3>
                        <p>
                            <strong>To the maximum extent permitted by law, Aureus Alliance Holdings (Pty) Ltd
                            cannot be held accountable for:</strong>
                        </p>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li><strong>Financial losses</strong> arising from share ownership, trading, or market conditions</li>
                            <li><strong>Death, injury, or personal harm</strong> not directly caused by Company negligence</li>
                            <li><strong>Business interruption</strong> or loss of profits or income</li>
                            <li><strong>Data loss, system failures,</strong> or cybersecurity incidents</li>
                            <li><strong>Third-party actions</strong> including payment processors, service providers</li>
                            <li><strong>Force majeure events</strong> including natural disasters, war, government actions</li>
                            <li><strong>Cryptocurrency volatility</strong> or payment processing failures</li>
                            <li><strong>Tax implications</strong> or changes in tax law affecting shareholders</li>
                            <li><strong>Regulatory changes</strong> affecting mining operations or share ownership</li>
                            <li><strong>Market volatility</strong> or economic downturns</li>
                            <li><strong>Currency fluctuations</strong> affecting gold prices or company operations</li>
                        </ul>
                        <p>
                            <strong>Maximum liability:</strong> The Company's total liability to any shareholder shall not exceed
                            the amount paid by that shareholder for shares, regardless of the cause of action or theory of liability.
                        </p>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">6. NO WARRANTIES</h2>
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-white">Disclaimer of Warranties</h3>
                        <p>
                            <strong>The Company provides all information and services "AS IS" without warranties of any kind.</strong>
                        </p>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li>No warranty of accuracy, completeness, or timeliness of information</li>
                            <li>No warranty of uninterrupted or error-free platform operation</li>
                            <li>No warranty of specific share returns or outcomes</li>
                            <li>No warranty regarding future gold prices or market conditions</li>
                            <li>No warranty of regulatory approval or compliance outcomes</li>
                        </ul>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">7. REGULATORY COMPLIANCE</h2>
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-white">Legal Framework</h3>
                        <p>
                            <strong>This disclaimer is governed by South African law.</strong> The Company operates under
                            South African jurisdiction and complies with applicable local regulations.
                        </p>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li>Subject to Companies Act 71 of 2008</li>
                            <li>Compliant with POPIA (Protection of Personal Information Act)</li>
                            <li>Adherent to mining and environmental regulations</li>
                            <li>Subject to SARS (South African Revenue Service) requirements</li>
                        </ul>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">8. CONTACT INFORMATION</h2>
                    <div className="space-y-2">
                        <p><strong className="text-white">Aureus Alliance Holdings (Pty) Ltd</strong></p>
                        <p><strong className="text-white">Registration Number:</strong> 2025/368711/07</p>
                        <p><strong className="text-white">General Inquiries:</strong> info@aureus.africa</p>
                        <p><strong className="text-white">General Support:</strong> support@aureus.africa</p>
                        <p><strong className="text-white">Address:</strong> 1848 Mees Avenue, Randpark Ridge, Randburg, Gauteng, 2169</p>
                        <p><strong className="text-white">Website:</strong> aureus.africa</p>
                    </div>
                </section>

                <div className="border-t border-gray-600 pt-6 mt-8">
                    <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                        <p className="text-center text-sm font-semibold text-red-400">
                            BY USING THIS PLATFORM OR PURCHASING SHARES, YOU ACKNOWLEDGE THAT YOU HAVE READ, 
                            UNDERSTOOD, AND AGREE TO BE BOUND BY THIS DISCLAIMER AND ALL ASSOCIATED RISKS.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
